import React, { useState, useEffect, useCallback, useMemo } from 'react';
import firebase from 'firebase/app';
import 'firebase/firestore';
import Observable from '../Observable/Observable';
import { setLoading } from '../LoadingContext/LoadingContext';
import { User } from '../AuthContext/AuthContext';
import {
  DepartmentType,
  ExternalType,
  ManifestDraft,
  ManifestReceived,
  OfficeType,
  ShipType,
} from '../../constants';
import { Home } from '../../model/Home/Home';

/**
 * Custom hook to interact with a Firestore collection using query configurations.
 *
 * @param {string} collection - The name of the Firestore collection.
 * @param {Object} [queryConfig={}] - Configuration for the query. Properties can include:
 *   - {number} [limit] - Limit on number of documents to retrieve.
 *   - {firebase.firestore.DocumentSnapshot} [startAt] - Starting value for a range query.
 *   - {Array<Object>} [where] - Array of query conditions in form of {field, op, value}.
 *   - {Object} [orderBy] - Array of order directives in form of {field: direction}.
 *   - {Array<Object>} [ids] - Array of document id's.
 * @returns {Array} An array with the first element being the data and the second being the loading state.
 *
 * @example
 * const queryConfig = {
 *   limit: 10,
 *   startAt: {},
 *   where: [{field: "status", op: "==", value: "active"}],
 *   orderBy: [["timestamp", "desc"]],
 * };
 * const [data, isLoading] = useCollection('users', queryConfig);
 */
export const useCollection = (collection, { limit, startAfter, startAt, where, orderBy } = {}) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const memoizedOrderBy = useMemo(() => orderBy, [JSON.stringify(orderBy)]);
  const memoizedWhere = useMemo(() => where, [JSON.stringify(where)]);

  const memoizedQueryFn = useCallback(() => {
    setIsLoading(true);
    let query = firebase.firestore().collection(collection);
    if (Array.isArray(orderBy)) {
      orderBy.forEach(([field, direction]) => {
        query = query.orderBy(field, direction);
      });
    }

    if (Array.isArray(where)) {
      where.forEach((where) => {
        const { field, op, value } = where;
        query = query.where(field, op, value);
      });
    }

    if (!!startAt) {
      query = query.startAt(startAt);
    } else if (!!startAfter) {
      query = query.startAfter(startAfter);
    }

    if (!!limit) {
      query = query.limit(limit);
    } else {
      console.warn('WARN: No limit specified for useCollection, please set a sensible default');
    }

    return query;
  }, [startAfter, startAt, limit, memoizedOrderBy, memoizedWhere]);

  useEffect(() => {
    const unsubscribe = memoizedQueryFn().onSnapshot(
      (snapshot) => {
        const newData = [
          ...snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
            _snapshot: doc,
          })),
        ];
        setData(newData);
        setIsLoading(false);
      },
      (err) => {
        console.error(err);
        setIsLoading(false);
        throw err;
      },
    );

    return () => unsubscribe();
  }, [memoizedQueryFn]);

  return [data, isLoading];
};

export const useCollectionByIds = (collection, ...ids) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const chunkSize = 10;

  const memoizedChunkedIds = useMemo(() => {
    return ids.length > 0
      ? Array.from({ length: Math.ceil(ids.length / chunkSize) }, (_, i) =>
          ids.slice(i * chunkSize, (i + 1) * chunkSize),
        )
      : [[]]; // Ensure at least one empty chunk to avoid breaking queries
  }, [JSON.stringify(ids)]);

  const memoizedQueries = useMemo(() => {
    return memoizedChunkedIds.map((chunk) => {
      if (chunk.length === 0) {
        return firebase
          .firestore()
          .collection(collection)
          .where(firebase.firestore.FieldPath.documentId(), '==', 'dummy-id-for-workaround');
      } else {
        return firebase
          .firestore()
          .collection(collection)
          .where(firebase.firestore.FieldPath.documentId(), 'in', chunk);
      }
    });
  }, [collection, memoizedChunkedIds]);

  useEffect(() => {
    setData([]);
    setIsLoading(true);

    let completedQueries = 0;
    const unsubscribes = memoizedQueries.map((query) =>
      query.onSnapshot(
        (snapshot) => {
          const newData = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
            _snapshot: doc,
          }));

          setData((prevData) => {
            const newIds = newData.map((v) => v.id);
            return [...prevData.filter((v) => !newIds.includes(v.id)), ...newData];
          });

          completedQueries += 1;
          if (completedQueries === memoizedQueries.length) {
            setIsLoading(false);
          }
        },
        (err) => {
          console.error(err);
        },
      ),
    );

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [memoizedQueries]);

  return [data, isLoading];
};

//Created to find assets using an array with storageIds
export const useCollectionByFieldInIDArray = (collection, field, ids) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const chunkSize = 10;
  const chunkedIds = [];

  // Split the IDs into chunks of 10 or fewer IDs
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    chunkedIds.push(chunk);
  }

  const memoizedQueries = useMemo(() => {
    return chunkedIds.map((chunk) => {
      if (chunk.length === 0) {
        // Workaround to retrieve an empty result set
        return firebase
          .firestore()
          .collection(collection)
          .where(firebase.firestore.FieldPath.documentId(), '==', 'dummy-id-for-workaround');
      } else {
        return firebase
          .firestore()
          .collection(collection)
          .where(field, 'in', chunk)
          .where('deleted', '==', false);
      }
    });
  }, [collection, JSON.stringify(chunkedIds)]);

  useEffect(() => {
    const unsubscribePromises = memoizedQueries.map((query) =>
      query.onSnapshot(
        (snapshot) => {
          const newData = [
            ...snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            })),
          ];
          setData((prevData) => {
            const newIds = newData.map((v) => v.id);
            return [...prevData.filter((v) => newIds.indexOf(v.id) === -1), ...newData];
          });
        },
        (err) => {
          console.log(err);
          throw err;
        },
      ),
    );

    Promise.all(unsubscribePromises)
      .then(() => {
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
        throw err;
      });

    return () => {
      unsubscribePromises.forEach((unsubscribePromise) => {
        unsubscribePromise();
      });
    };
  }, [memoizedQueries]);

  return [data, isLoading];
};

/**
 * Custom hook to fetch a document from a Firestore collection.
 *
 * @param {string} collection - The name of the collection.
 * @param {string} id - The ID of the document.
 * @returns {[Object, boolean]} - Returns the document data and loading state
 * @example
 * const [data, isLoading, error] = useDocument('users', '12345');
 */
export const useDocument = (collection, id) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const trimSlashes = (str) => {
    return str
      .split('/')
      .filter((c) => c)
      .join('/');
  };

  useEffect(() => {
    if (!id) {
      setData(null);
      setIsLoading(false);
      return;
    }
    const docRef = firebase.firestore().doc(trimSlashes(collection) + '/' + id);

    return docRef.onSnapshot(
      (snapshot) => {
        if (snapshot.exists) {
          setData({ ...snapshot.data(), id: snapshot.id, _snapshot: snapshot });
        }
        setIsLoading(false);
      },
      (err) => {
        console.log(err);
        setIsLoading(false);
        throw err;
      },
    );
  }, [collection, id]);

  return [data, isLoading];
};

/**
 * Custom hook to interact with Firestore's "manifests" collection.
 *
 * @param {function(firebase.firestore.Query | firebase.firestore.CollectionReference): firebase.firestore.Query} fn - A function that takes a Firestore CollectionReference as argument and returns a resource.
 * @returns {any} - Returns the resource obtained from useCollection.
 *
 * @example
 * const resource = useManifests((collection) => collection.where("field", "==", "value"));
 */
export const useManifests = (queryConfig) => {
  return useCollection('manifests', queryConfig);
};

export const useManifestsByIds = (...ids) => {
  return useCollectionByIds('manifests', ...ids);
};

export const useManifest = (id) => {
  return useDocument('manifests', id);
};

/**@deprecated**/
export const useDecoratedManifest = (id) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [manifest, manifestLoading] = useDocument('manifests', id);
  const [addressables, addressablesLoading] = useAddressables();

  useEffect(() => {
    if (manifestLoading || addressablesLoading) {
      setIsLoading(true);
      return;
    }
    if (!manifest) {
      setIsLoading(false);
      return;
    }
    setData({
      ...manifest,
      recipient: addressables[manifest.recipientId],
      sender: addressables[manifest.senderId],
    });
    setIsLoading(false);
  }, [manifest, addressables, manifestLoading, addressablesLoading]);

  return [data, isLoading];
};

/**
 * Custom hook to fetch and decorate manifests with additional data.
 *
 * @function useDecoratedManifests
 * @param {Function} queryFn - The function that determines how the manifests are queried.
 * @param {Array} deps - Dependency array for effect hooks, typically a list of variables that can affect the outcome of the query function.
 *
 * @returns {Array} An array containing:
 * - {Array} data: The decorated manifests list.
 * - {boolean} isLoading: Indicates if the manifests and addressables are still being fetched.
 *
 * @example
 * const [manifests, loading] = useDecoratedManifests(myQueryFunction, [someDependencies]);
 */
export const useDecoratedManifests = (manifestHook) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [addressables, addressablesLoading] = useAddressables();
  const [manifests, manifestsLoading] = manifestHook;

  useEffect(() => {
    if (manifestsLoading || addressablesLoading) {
      setIsLoading(true);
      return;
    }

    setData(
      manifests.map((m) => {
        return {
          ...m,
          recipient: addressables[m.recipientId],
          sender: addressables[m.senderId],
        };
      }),
    );
    setIsLoading(false);
  }, [manifests, addressables, manifestsLoading, addressablesLoading]);

  return [data, isLoading];
};

/**
 * Returns the home view for a given home id
 * @param id
 * @returns {[Home,boolean]} the home and loading state
 */
export const useHome = (id) => {
  const [home, homeLoading] = useDocument('homes', id);
  const [assets, assetsLoading] = useCollection(`homes/${id}/assets`);
  const [manifests, manifestsLoading] = useCollection(`homes/${id}/manifests`);
  const [storageIds, storageIdsLoading] = useStorageIds([id]);
  const [addressables, addressablesLoading] = useAddressables();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      setData(null);
      setLoading(false);
      return;
    }

    if (
      !homeLoading &&
      !assetsLoading &&
      !manifestsLoading &&
      !storageIdsLoading &&
      !addressablesLoading
    ) {
      setLoading(false);

      // Create list of Root Storage, and departments
      let departmentIds = storageIds.map((storage) => storage.id);

      // Add addressables
      let manifestWithAddressables = manifests.map((m) => ({
        ...m,
        recipient: addressables[m.recipient.id],
        sender: addressables[m.sender.id],
      }));

      setData(
        new Home({
          ...home,
          departmentIds,
          manifests: manifestWithAddressables,
          assets,
        }),
      );
    }
  }, [
    id,
    homeLoading,
    assetsLoading,
    manifestsLoading,
    storageIdsLoading,
    addressablesLoading,
    home,
    assets,
    manifests,
    storageIds,
    addressables,
  ]);

  return [data, loading];
};

export const useStorageIds = (ids) => {
  const [storages, storagesLoading] = useCollection('storages', {
    where: [{ field: 'lineage', op: 'array-contains-any', value: ids }],
  });

  return [storages, storagesLoading];
};

export const useStoragesAssociated = (storageId) => {
  return useCollection('storages', {
    where: [{ field: 'lineage', op: 'array-contains', value: storageId }],
  });
};

export const useShip = (shipId) => {
  return useDocument('ships', shipId);
};
export const useShips = (queryConfig) => {
  return useCollection('ships', queryConfig);
};
export const useOffice = (officeId) => {
  return useDocument('offices', officeId);
};

export const useOffices = (queryConfig) => {
  return useCollection('offices', queryConfig);
};

export const useDepartment = (departmentId) => {
  return useDocument('departments', departmentId);
};
export const useDepartments = (queryConfig) => {
  return useCollection('departments', queryConfig);
};

export const useExternal = (externalId) => {
  return useDocument('externals', externalId);
};
export const useExternals = (queryConfig) => {
  return useCollection('externals', queryConfig);
};

export const useProducts = (queryConfig) => {
  return useCollection('products', queryConfig);
};
export const useProduct = (productId) => {
  return useDocument('products', productId);
};
export const useUsers = (queryConfig) => {
  return useCollection('users', queryConfig);
};
export const useUser = (userId) => {
  return useDocument('users', userId);
};

// Custom hook that takes a collection hook and maps it to a map and loading state
export const useCollectionMap = (collectionHook, key = 'id') => {
  const [collection, loading] = collectionHook;
  const [map, setMap] = useState({});
  const [isLoading, setIsLoading] = useState(loading);

  useEffect(() => {
    // When the collection or loading state changes, update the map and isLoading state
    setIsLoading(loading);
    const newMap = {};
    collection.forEach((item) => {
      newMap[item[key]] = item;
    });
    setMap(newMap);
  }, [collection, loading, key]);

  return [map, isLoading];
};

export const useAssets = (queryConfig) => {
  return useCollection('assets', queryConfig);
};
export const useAssetsByIds = (assetsIds) => {
  return useCollectionByIds('assets', ...assetsIds);
};

export const useDecoratedAssetsByStorageIds = (storageIds) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [assets, assetsLoading] = useCollectionByFieldInIDArray('assets', 'storageId', storageIds);
  const [products, productsLoading] = useProducts();
  const [storages, storagesLoading] = useDecoratedStorages();

  useEffect(() => {
    if (assetsLoading || productsLoading || storagesLoading) {
      return;
    }
    setData(
      assets.map((asset) => {
        return {
          ...asset,
          product: products.find((obj) => obj.id === asset.productId),
          storage: storages.find((obj) => obj.id === asset.storageId),
        };
      }),
    );
    setIsLoading(false);
  }, [products, assets, storages]);
  return [data, isLoading];
};

export const useDecoratedAssets = (queryConfig) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [assets, assetsLoading] = useAssets(queryConfig);
  const [products, productsLoading] = useProducts();
  const [storages, storagesLoading] = useDecoratedStorages();

  useEffect(() => {
    if (assetsLoading || productsLoading || storagesLoading) {
      setIsLoading(true);
      return;
    }
    setData(
      assets.map((asset) => {
        let product = products.find((obj) => obj.id === asset.productId);
        let storage = storages.find((obj) => obj.id === asset.storageId);
        const { _snapshot, ...rest } = asset; //omit _snapshot
        return {
          ...rest,
          product: product,
          storage: storage,
          productName: product?.name,
          storageName: storage?.name,
        };
      }),
    );
    setIsLoading(false);
  }, [products, assets, storages]);
  return [data, isLoading];
};

export const useDecoratedAsset = (id) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [asset, assetLoading] = useDocument('assets', id);
  const [productId, setProductId] = useState('');
  const [storageId, setStorageId] = useState('');
  const [product, productLoading] = useProduct(productId);
  const [storage, storageLoading] = useDecoratedStorage(storageId);

  useEffect(() => {
    if (assetLoading || !asset) {
      return;
    }
    setProductId(asset.productId);
    setStorageId(asset.storageId);
  }, [assetLoading, asset]);

  useEffect(() => {
    if (assetLoading || productLoading || storageLoading) {
      setIsLoading(true);
      return;
    }
    setData({
      ...asset,
      product: product,
      storage: storage,
    });
    setIsLoading(false);
  }, [product, asset, storage, storageLoading, productLoading, assetLoading]);
  return [data, isLoading];
};

export const useStorages = (queryConfig) => {
  return useCollection('storages', queryConfig);
};

export const useDecoratedStorages = (queryConfig) => {
  const [storages, storagesLoading] = useCollection('storages', queryConfig);

  const decorated = useMemo(() => {
    if (storagesLoading) {
      return [];
    }

    const nameById = storages.reduce((map, s) => {
      map[s.id] = s.name;
      return map;
    }, {});

    return storages.map((s) => ({
      ...s,
      lineageNames: s.lineage.map((id) => {
        const name = nameById[id];
        if (!name) {
          console.warn(`useDecoratedStorages: missing name for lineage ID "${id}"`);
        }
        return name;
      }),
    }));
  }, [storages, storagesLoading]);

  return [decorated, storagesLoading];
};

export const useAddressableNames = () => {
  const [storages, storagesLoading] = useCollection('storages', {
    where: [{ field: 'ownerType', op: '>', value: 0 }], //Disregards simple storages
  });
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (!storagesLoading) {
      setIsLoading(false);

      const newMap = {};
      storages.forEach((storage) => {
        //Departments
        if (storage.ownerType === 4) {
          newMap[storage.id] =
            storages.find((obj) => obj.id === storage.lineage[0]).name + ' ' + storage.name;
          return;
        }
        //Ships, Offices, Externals
        newMap[storage.id] = storage.name;
        return;
      });
      setData(newMap);
    }
  }, [storagesLoading]);
  return [data, isLoading];
};

export const useStorage = (id) => {
  return useDocument('storages', id);
};

/**
 * Gets storage by ID.
 *
 * @param {string} id - The ID of the storage item to fetch.
 * @returns {[Object, boolean]} - Returns an array containing the storage and a loading state.
 */
export const useDecoratedStorage = (id) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [storage, storageLoading] = useDocument('storages', id);
  const [storageIds, setStorageids] = useState([]);
  const [lineageStorages, lineageStoragesLoading] = useCollectionByIds('storages', ...storageIds);

  useEffect(() => {
    if (!id) {
      setData(null);
      setIsLoading(false);
    } else if (!storageLoading && storage) {
      setStorageids(storage.lineage);
    } else {
      setData(storage);
      setIsLoading(false);
    }
  }, [storageLoading, storage]);

  useEffect(() => {
    if (storageLoading || lineageStoragesLoading) {
      setIsLoading(true);
      return;
    }
    if (storage != null && lineageStorages.length !== 0) {
      setIsLoading(false);
      setData({
        ...storage,
        lineageNames: storage.lineage.map((v) => lineageStorages.find((obj) => obj.id === v)?.name),
      });
    } else {
      setIsLoading(false);
      setData(storage);
    }
    setIsLoading(false);
  }, [lineageStorages, storage, storageLoading, lineageStoragesLoading]);
  return [data, isLoading];
};

export const useEventLogs = (queryConfig) => {
  return useCollection('eventlog', queryConfig);
};

/**
 * FIXME: Why is this not using the useCollections hook?
 * Custom hook to fetch all event logs within a specified date range from Firestore.
 *
 * @param {Date} fromDate - The start date for fetching event logs.
 * @param {Date} toDate - The end date for fetching event logs.
 * @returns {[Array, boolean]} - Returns an array of event logs and a loading state.
 *
 * @example
 * const [events, loading] = useEventLogsAll(new Date('2023-01-01'), new Date('2023-01-31'));
 */
export const useEventLogsAll = (fromDate, toDate, limit) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = firebase
      .firestore()
      .collection('eventlog')
      .orderBy('timestamp', 'desc')
      .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(fromDate))
      .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(toDate))
      .limit(limit)
      .onSnapshot(
        (snapshot) => {
          const eventsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            _snapshot: doc,
          }));
          setEvents(eventsData);
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching event logs: ', error);
          setLoading(false);
        },
      );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [fromDate, toDate]);

  return [events, loading];
};

export const useAddressables = () => {
  const [offices, officesLoading] = useOffices();
  const [ships, shipsLoading] = useShips();
  const [departments, departmentsLoading] = useDepartments();
  const [externals, externalsLoading] = useExternals();
  const loading = officesLoading || shipsLoading || departmentsLoading || externalsLoading;

  const withType = (items, type) =>
    Object.fromEntries(
      items.map((item) => ({ _type: type, ...item })).map((item) => [item.id, item]),
    );

  const addressables = useMemo(() => {
    if (loading) return [];

    return {
      ...withType(ships, ShipType),
      ...withType(offices, OfficeType),
      ...withType(externals, ExternalType),
      ...withType(
        departments.map((department) => {
          const parent = [...ships, ...offices].find((v) => v.id === department.parent);
          return {
            ...department,
            name: [parent?.name, department.name].filter(Boolean).join(' '),
          };
        }),
        DepartmentType,
      ),
    };
  }, [loading, offices, ships, departments, externals]);

  return [addressables, loading];
};

export const useFiles = (queryConfig) => {
  return useCollection('files', queryConfig);
};

export const useTasks = (queryConfig) => {
  return useCollection('tasks', queryConfig);
};

export const useSearchIndex = (query, collection, limit = 25) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      if (query && query.length >= 3) {
        const words = query && query.toLowerCase().toString().split(' ');
        let q = firebase
          .firestore()
          .collection('search_index')
          .orderBy('snapRef.CreateTime', 'desc')
          .where('words', 'array-contains-any', words)
          .limit(limit);
        if (!!collection) {
          q = q.where('collection', '==', collection);
        }
        const result = await q.get();
        setResult(() => result.docs.map((d) => d.get('snapRef.Ref')));
      }
      setLoading(false);
    };
    fetchData();
  }, [query]);
  return [result, loading];
}; /*@deprecated*/ /*/*@deprecated*/
export const UserDocument = (id) => FirestoreQuery.get(firebase.firestore().doc('users/' + id));

/*@deprecated*/
export const UserResource = ({ id, children }) => (
  <FirestoreResource query={UserDocument(id)}>{children}</FirestoreResource>
);

/*@deprecated*/
export class FirestoreQuery extends Observable {
  static cache = [];

  data;
  dataReady;
  query;
  queryUnsubscribe;
  teardownTimeout;

  constructor(query) {
    super();
    if ((!query) instanceof firebase.firestore.Query) {
      throw Error('invalid firestore query');
    }
    this.query = query;
    if (
      !FirestoreQuery.cache.find(
        (v) => v.query.constructor === query.constructor && v.query.isEqual(query),
      )
    ) {
      FirestoreQuery.cache.push(this);
    }
  }

  static get(query) {
    if ((!query) instanceof firebase.firestore.Query) {
      throw Error('invalid firestore query');
    }

    let fq = FirestoreQuery.cache.find(
      (v) => v.query.constructor === query.constructor && v.query.isEqual(query),
    );
    if (!fq) {
      fq = new FirestoreQuery(query);
    }
    return fq;
  }

  init() {
    if (!this.queryUnsubscribe) {
      this.queryUnsubscribe = this.query.onSnapshot(
        (snapshot) => {
          this.dataReady = true;
          this.read(snapshot, this.notify.bind(this));
        },
        (err) => {
          throw err;
        },
      );
    }
  }

  read(snapshot, f) {
    if (snapshot instanceof firebase.firestore.QuerySnapshot) {
      if (!this.data) {
        this.data = Resource.from([]);
      }
      this.data.splice(0, this.data.length);
      this.data.push(...snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    } else if (snapshot instanceof firebase.firestore.DocumentSnapshot) {
      this.data = { ...snapshot.data(), id: snapshot.id };
    } else {
      throw Error('unknown snapshot type');
    }
    f(this.data);
  }

  subscribe(f) {
    clearTimeout(this.teardownTimeout);
    this.init();
    if (this.dataReady) {
      this.query.get({ source: 'cache' }).then((snapshot) => {
        this.read(snapshot, f);
      });
    }
    return super.subscribe(f);
  }

  subscribeOnce(f) {
    return super.subscribeOnce(f);
  }

  unsubscribe(f) {
    super.unsubscribe(f);
    if (this.observers.length === 0) {
      clearTimeout(this.teardownTimeout);
      this.teardownTimeout = setTimeout(this.teardown.bind(this), 60000);
    }
  }

  teardown() {
    if (this.queryUnsubscribe) {
      this.queryUnsubscribe();
      this.queryUnsubscribe = undefined;
    }
    this.dataReady = false;
  }

  isEqual(other) {
    return this.constructor === other.constructor && this.query.isEqual(other.query);
  }
}

/*@deprecated*/
export class FirestoreResource extends React.Component {
  constructor(props) {
    super(props);
    this.state = { data: null };

    if (!(this.props.query instanceof FirestoreQuery)) {
      throw Error('expected FirestoreQuery instance');
    }
  }

  fetchData(query) {
    const load$ = query.subscribeOnce(setLoading());
    const data$ = query.subscribe((data) => {
      this.setState({ data });
    });
    this.unsubscribe = () => {
      load$();
      data$();
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.query.isEqual(nextProps.query)) {
      return;
    }

    const { query } = nextProps;
    this.unsubscribe();
    this.fetchData(query);
  }

  componentDidMount() {
    const { query } = this.props;
    this.fetchData(query);
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  render() {
    const { children } = this.props;
    const { data } = this.state;
    if (!data) {
      return <></>;
    }
    return children(data);
  }
}

/*@deprecated*/
export class Resource extends Array {
  constructor(...args) {
    super(...args);
    if (!args) return;

    const index = {};
    let touched = false;

    this.indexAdd = (...items) => {
      touched = true;
      for (let i = items.length - 1; i >= 0; i--) {
        index[items[i].id] = items[i];
      }
    };

    this.indexRemove = (...items) => {
      for (let i = items.length - 1; i >= 0; i--) {
        delete (index, items[i].id);
      }
    };

    this.get = (id) => {
      if (this.length > 0 && !touched) {
        this.indexAdd(...this);
      }
      return index[id];
    };

    this.hash = () => {
      return btoa(JSON.stringify(Object.keys(index)));
    };

    if (args.length === 1 && typeof args[0] === 'number') return;
    for (let i = args.length - 1; i >= 0; i--) {
      this.indexAdd(args[i]);
    }
  }

  push(...items) {
    const v = super.push(...items);
    this.indexAdd(...items);
    return v;
  }

  splice(start, deleteCount) {
    const v = super.splice(start, deleteCount);
    this.indexRemove(...v);
    return v;
  }

  pop() {
    const v = super.pop();
    this.indexRemove(v);
    return v;
  }

  shift() {
    const v = super.shift();
    this.indexRemove(v);
    return v;
  }

  slice(start, end) {
    return new this.constructor(...super.slice(start, end));
  }

  unshift(...items) {
    const v = super.unshift(...items);
    this.indexAdd(...items);
    return v;
  }

  fill(value, start, end) {
    this.index(value);
    return super.fill(value, start, end);
  }
}
